import type { ChangeEvent, FC, ReactNode } from 'react'

import { useId, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import {
    Camera,
    Check,
    LoaderCircle,
    X,
    Zap
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { VISIT_SLOTS } from '@/constants/quote-form'
import { siteConfig } from '@/constants/site'

interface OptionGridProps {
    options: string[]
    value: string | string[] | undefined
    onChange: (value: string) => void
    multiple?: boolean
    columns?: 1 | 2 | 3
    labelledBy?: string
    invalid?: boolean
}

export const OptionGrid: FC<OptionGridProps> = ({
    options,
    value,
    onChange,
    multiple = false,
    columns = 2,
    labelledBy,
    invalid = false
}): ReactNode => {
    const isSelected = (option: string): boolean =>
        Array.isArray(value) ? value.includes(option) : value === option

    return (
        <div
            role={multiple ? 'group' : 'radiogroup'}
            aria-labelledby={labelledBy}
            aria-invalid={invalid || undefined}
            className={cn(
                'grid gap-2',
                columns >= 2 && 'sm:grid-cols-2',
                columns === 3 && 'lg:grid-cols-3'
            )}
        >
            {options.map(option => {
                const selected = isSelected(option)

                return (
                    <button
                        key={option}
                        type='button'
                        role={multiple ? 'checkbox' : 'radio'}
                        aria-checked={selected}
                        onClick={() => onChange(option)}
                        className={cn(
                            'flex min-h-14 items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-[15px] font-medium transition-[background-color,border-color,color] duration-300',
                            selected ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-card text-ink hover:border-ink/35'
                        )}
                    >
                        {option}

                        <span
                            aria-hidden='true'
                            className={cn(
                                'flex size-5 shrink-0 items-center justify-center border transition-colors',
                                multiple ? 'rounded-md' : 'rounded-full',
                                selected ? 'border-ember bg-ember text-white' : 'border-ink/25'
                            )}
                        >
                            <Check className={cn('size-3 transition-transform', selected ? 'scale-100' : 'scale-0')} />
                        </span>
                    </button>
                )
            })}
        </div>
    )
}

interface TextFieldProps {
    label: string
    value: string
    onChange: (value: string) => void
    type?: 'text' | 'tel' | 'email'
    autoComplete?: string
    inputMode?: 'text' | 'tel' | 'email' | 'numeric'
    placeholder?: string
    error?: string
    optional?: boolean
    hint?: string
    maxLength?: number
}

export const TextField: FC<TextFieldProps> = ({
    label,
    value,
    onChange,
    type = 'text',
    autoComplete,
    inputMode,
    placeholder,
    error,
    optional = false,
    hint,
    maxLength = 120
}): ReactNode => {
    const id = useId()

    return (
        <div>
            <label htmlFor={id} className='flex items-baseline justify-between text-sm font-medium text-ink'>
                {label}
                {optional && <span className='text-[12.5px] font-normal text-slate'>Optional</span>}
            </label>

            <input
                id={id}
                type={type}
                value={value}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                autoComplete={autoComplete}
                inputMode={inputMode}
                placeholder={placeholder}
                maxLength={maxLength}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
                className={cn(
                    'mt-2 h-13 w-full rounded-2xl border bg-card px-4 text-[16px] text-ink outline-none transition-colors placeholder:text-slate/55 focus:border-ember',
                    error ? 'border-ember' : 'border-ink/15'
                )}
            />

            {hint && !error && <p id={`${id}-hint`} className='mt-1.5 text-[13px] text-slate'>{hint}</p>}
            {error && <p id={`${id}-error`} className='mt-1.5 text-[13px] text-ember'>{error}</p>}
        </div>
    )
}

const MAX_PHOTO_EDGE = 1600

const resizeImage = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onerror = () => reject(new Error('read'))
        reader.onload = () => {
            const image = new window.Image()

            image.onerror = () => reject(new Error('decode'))
            image.onload = () => {
                const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(image.width, image.height))
                const canvas = document.createElement('canvas')

                canvas.width = Math.round(image.width * scale)
                canvas.height = Math.round(image.height * scale)

                const context = canvas.getContext('2d')

                if (!context) {
                    reject(new Error('canvas'))

                    return
                }

                context.drawImage(image, 0, 0, canvas.width, canvas.height)
                resolve(canvas.toDataURL('image/jpeg', 0.82))
            }

            image.src = String(reader.result)
        }

        reader.readAsDataURL(file)
    })

interface PhotoInputProps {
    label: string
    value: { dataUrl: string, name: string } | null
    onChange: (value: { dataUrl: string, name: string } | null) => void
}

export const PhotoInput: FC<PhotoInputProps> = ({ label, value, onChange }): ReactNode => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const id = useId()

    const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]

        event.target.value = ''

        if (!file) return

        if (!file.type.startsWith('image/')) {
            setError("That file doesn't look like a photo. Please choose a JPEG or PNG.")

            return
        }

        setBusy(true)
        setError('')

        try {
            const dataUrl = await resizeImage(file)

            onChange({ dataUrl, name: file.name })
        }
        catch {
            setError("That photo wouldn't open on our end. Please try a JPEG or PNG.")
        }
        finally {
            setBusy(false)
        }
    }

    return (
        <div>
            <p id={`${id}-label`} className='text-sm font-medium text-ink'>{label}</p>
            <p className='mt-1 text-[13px] text-slate'>One clear photo lets your technician load the right parts before setting out.</p>

            <input
                ref={inputRef}
                id={id}
                type='file'
                accept='image/*'
                onChange={handleFile}
                className='sr-only'
                aria-labelledby={`${id}-label`}
            />

            {value ? (
                <div className='mt-3 flex items-center gap-4 rounded-2xl border border-ink/12 bg-card p-3'>
                    <span className='relative size-16 shrink-0 overflow-hidden rounded-xl bg-sand'>
                        <Image src={value.dataUrl} alt="The photo you added" fill unoptimized className='object-cover' />
                    </span>

                    <span className='min-w-0 flex-1 truncate text-sm text-ink'>{value.name}</span>

                    <button
                        type='button'
                        onClick={() => onChange(null)}
                        aria-label="Remove photo"
                        className='flex size-9 items-center justify-center rounded-full text-slate transition-colors hover:bg-sand hover:text-ink'
                    >
                        <X className='size-4' />
                    </button>
                </div>
            ) : (
                <button
                    type='button'
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    className='mt-3 flex w-full items-center justify-center gap-3 rounded-2xl border border-dashed border-ink/25 bg-card px-4 py-6 text-[15px] font-medium text-ink transition-colors hover:border-ink/50 hover:bg-sand/40 disabled:opacity-60'
                >
                    {busy ? <LoaderCircle className='size-5 animate-spin' aria-hidden='true' /> : <Camera className='size-5' aria-hidden='true' />}
                    {busy ? "Getting your photo ready" : "Choose a photo"}
                </button>
            )}

            {error && <p className='mt-2 text-[13px] text-ember'>{error}</p>}
        </div>
    )
}

export const ASAP_SLOT = 'ASAP'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const SLOT_START_HOURS = [8, 10, 12, 14, 16]

type VisitDay = { key: string, label: string, sub: string, isToday: boolean }

const buildDays = (): VisitDay[] => {
    const days: VisitDay[] = []
    const cursor = new Date()
    const now = new Date()

    while (days.length < 6) {
        const isToday = cursor.toDateString() === now.toDateString()
        const isTomorrow = cursor.getDate() === new Date(now.getTime() + 86400000).getDate() && !isToday
        const dayIndex = cursor.getDay()
        const lastSlotStart = SLOT_START_HOURS[SLOT_START_HOURS.length - 1]

        if (dayIndex !== 0 && !(isToday && now.getHours() >= lastSlotStart)) {
            days.push({
                key: `${cursor.getFullYear()}-${cursor.getMonth() + 1}-${cursor.getDate()}`,
                label: isToday ? "Today" : isTomorrow ? "Tomorrow" : DAY_NAMES[dayIndex],
                sub: `${cursor.getMonth() + 1}/${cursor.getDate()}`,
                isToday
            })
        }

        cursor.setDate(cursor.getDate() + 1)
    }

    return days
}

interface TimingPickerProps {
    value: { day: string, slot: string }
    onChange: (value: { day: string, slot: string, label: string }) => void
    showAsap: boolean
    asapPreselected?: boolean
}

export const TimingPicker: FC<TimingPickerProps> = ({ value, onChange, showAsap, asapPreselected = false }): ReactNode => {
    const days = useMemo(buildDays, [])
    const activeDay = days.find(day => day.key === value.day) ?? days[0]
    const now = new Date()

    const slots = VISIT_SLOTS.map((slot, index) => ({
        slot,
        disabled: Boolean(activeDay?.isToday && now.getHours() >= SLOT_START_HOURS[index])
    }))

    return (
        <div className='space-y-5'>
            {showAsap && (
                <button
                    type='button'
                    aria-pressed={value.slot === ASAP_SLOT}
                    onClick={() => onChange({ day: '', slot: value.slot === ASAP_SLOT ? '' : ASAP_SLOT, label: "as soon as possible" })}
                    className={cn(
                        'flex w-full items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors duration-300',
                        value.slot === ASAP_SLOT ? 'border-ember bg-ember text-white' : 'border-ink/12 bg-card text-ink hover:border-ink/35'
                    )}
                >
                    <Zap className='size-5 shrink-0' aria-hidden='true' />

                    <span>
                        <span className='block text-[15.5px] font-semibold'>As soon as possible</span>
                        <span className={cn('block text-[13.5px]', value.slot === ASAP_SLOT ? 'text-white/80' : 'text-slate')}>
                            Technicians on call until {siteConfig.emergencyUntil}
                            {asapPreselected && value.slot === ASAP_SLOT ? ". Selected for you, since you need help today." : ""}
                        </span>
                    </span>
                </button>
            )}

            {value.slot !== ASAP_SLOT && (
                <>
                    <div className='no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1'>
                        {days.map(day => (
                            <button
                                key={day.key}
                                type='button'
                                aria-pressed={activeDay?.key === day.key}
                                onClick={() => onChange({ day: day.key, slot: '', label: '' })}
                                className={cn(
                                    'flex min-w-[5.5rem] shrink-0 flex-col items-center rounded-2xl border px-3 py-3 transition-colors duration-300',
                                    activeDay?.key === day.key ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-card text-ink hover:border-ink/35'
                                )}
                            >
                                <span className='text-[14.5px] font-semibold'>{day.label}</span>
                                <span className={cn('font-mono text-[11px]', activeDay?.key === day.key ? 'text-cream/60' : 'text-slate')}>{day.sub}</span>
                            </button>
                        ))}
                    </div>

                    <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
                        {slots.map(({ slot, disabled }) => {
                            const selected = value.slot === slot && (value.day === activeDay?.key || (!value.day && activeDay === days[0]))

                            return (
                                <button
                                    key={slot}
                                    type='button'
                                    disabled={disabled}
                                    aria-pressed={selected}
                                    onClick={() => activeDay && onChange({ day: activeDay.key, slot, label: `${activeDay.label} ${activeDay.sub}, ${slot}` })}
                                    className={cn(
                                        'h-12 rounded-2xl border text-[14px] font-medium transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-35',
                                        selected ? 'border-ember bg-ember text-white' : 'border-ink/12 bg-card text-ink hover:border-ink/35'
                                    )}
                                >
                                    {slot}
                                </button>
                            )
                        })}
                    </div>

                    <p className='text-[13px] text-slate'>
                        Choose the two-hour window that works best. A text confirms the exact arrival time.
                    </p>
                </>
            )}
        </div>
    )
}
